import styles from "./styles.module.scss";
import {TextMedium16, TextMedium20, ThirtyText} from "@/app/components/texts";
import {s} from "@/app/utils/s";
import {t} from "i18next";
import Slider from "@/app/components/slider";
import React, {memo, useEffect, useState} from "react";
import MapWithRadius from "@/app/components/map_static_with_radius";
import {useDebouncedValue} from "@/app/utils/use_debounced_value";
import Button from "@/app/components/button";
import {UpdateUserLocationModel} from "@/app/api/user";
import CityAutocomplete from "@/app/components/city_autocomplete/widget";

interface CityData {
  name: string;
  lat: number;
  lng: number;
  fullName: string;
}

interface Props {
  setRadius: (value: number) => void
  initialValue?: number
  goToFindPlayers: () => void
  coords?: UpdateUserLocationModel
  coordsError?: string
  getCoords: () => void
  onCitySelect: (city: CityData) => void
  selectedCity?: CityData
  isUsingCityCoords: boolean
}

const MyPadelLocation: React.FC<Props> = ({
                                            setRadius,
                                            initialValue,
                                            goToFindPlayers,
                                            coords,
                                            getCoords,
                                            coordsError,
                                            onCitySelect,
                                            selectedCity,
                                            isUsingCityCoords
                                          }) => {
  const [value, setValue] = useState(Math.floor(((initialValue ?? 0.8) / 1.6 > 20 ? 20 : (initialValue ?? 0.8) / 1.6) * 10) / 10);
  const debouncedValue = useDebouncedValue(value, 1000);

  useEffect(() => {
    setRadius(Math.floor((debouncedValue * 1.6) * 10) / 10);
  }, [debouncedValue, setRadius]);

  // Check if geolocation is available (no error and coords exist and not using city coords)
  const hasGeolocation = !coordsError && coords && !isUsingCityCoords;

  return (
    <div className={styles.myPadelLocation}>
      <ThirtyText text={t(s.myPadleLocation)}/>
      <div className={styles.textEndSlider}>
        <TextMedium16 text={t(s.searchMatchesInMilesFromMe, {miles: value.toFixed(1)})}/>
        <Slider value={value} onChange={setValue} min={0.5} max={20} step={0.5}/>
      </div>
      
      {hasGeolocation ? (
        // Full width map when geolocation is available
        <div className={styles.mapStack}>
          <MapWithRadius coords={coords} radiusKm={value} getCoords={getCoords} error={coordsError}/>
          <div className={styles.mapLabel}>
            <TextMedium20 text={t(s.searchMatchesInMilesFromMe, {miles: value.toFixed(1)})}/>
            <Slider value={value} onChange={setValue} min={0.5} max={20} step={0.5}/>
          </div>
        </div>
      ) : (
        // Full width map with overlay city input when no geolocation
        <div className={styles.mapStack}>
          <MapWithRadius coords={coords} radiusKm={value} getCoords={getCoords} error={undefined}/>
          <div className={styles.mapLabel}>
            <TextMedium20 text={t(s.searchMatchesInMilesFromMe, {miles: value.toFixed(1)})}/>
            <Slider value={value} onChange={setValue} min={0.5} max={20} step={0.5}/>
          </div>
          <div className={styles.cityOverlay}>
            <CityAutocomplete 
              onCitySelect={onCitySelect}
              value={selectedCity?.name}
              isWhite={true}
            />
          </div>
          {coordsError && (
            <div className={styles.mobileErrorContainer}>
              <TextMedium16 text={coordsError}/>
              <Button text={t(s.tryAgain)} onClick={getCoords}/>
            </div>
          )}
        </div>
      )}
      
      <Button text={t(s.findPlayers)} onClick={goToFindPlayers} className={styles.hide}/>
    </div>
  );
};

export default memo(MyPadelLocation);